import React, { useCallback, useState } from 'react'

import { GridReadyEvent, IDatasource, IGetRowsParams } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.min.css'
import 'ag-grid-community/styles/ag-theme-alpine.min.css'
import 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'

import { WorkViewsSkeleton } from './WorkViewsSkeleton'
import { columnTypes } from './columnTypes'
import { parseFiltersAndSorting } from './filterParser'
import { useWQGrid } from './useWQGrid'
import {
  setAddContactDrawerOpen,
  setCurrentStreamingNotePage,
  setDealAdvisorOpen,
  setDealAdvisorTab,
} from '@/client/generalSlice'
import { useAppDispatch, useAppSelector } from '@/client/hooks'
import { setSelectedAppId } from '@/client/workQueuesSlice'
import { streamingNotesPageSize } from '@/constants/streamingNotes'
import ClosedLostDrawer from '@/features/ClosedLossDrawer'
import { AddContactDrawer } from '@/features/FileContacts/AddContactDrawer'
import { useCurrentApplication } from '@/graphql/services/application'
import { useLazyGetAggregatedFieldQuery, useLazyGetWorkQueuesQuery } from '@/services/application'
import { useGetLoggedUserQuery } from '@/services/user'
import '@/styles/ag-grid.css'
import { isWorkviewsAdminOrSuperuser } from '@/utils/user'

export function WQGrid({
  rowsPerPage = 100,
  gridRef,
  setVisibleColCount,
  setTotalDeals,
  setAggregateByInvesmentPayment,
}) {
  const [getApplications] = useLazyGetWorkQueuesQuery()
  const [getAggregatedField] = useLazyGetAggregatedFieldQuery()
  const { data: loggedUser } = useGetLoggedUserQuery()
  const { getRowId, defaultColDef, columnDefs, isLoading: isLoadingFields } = useWQGrid()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState(null)
  const addContactDrawerOpen = useAppSelector((state) => state.general.addContactDrawerOpen)

  const { data: application } = useCurrentApplication()

  const isClosedLossModal = useAppSelector((state) => state.general.closedLossDrawerOpen)
  const queries = useAppSelector((state) => state.baseApi.queries)

  const initialParams = {
    page: 1,
    page_size: rowsPerPage,
    order_by: '-modified_at',
  }

  const onGridReady = useCallback(
    (gridReadyParams: GridReadyEvent) => {
      setLoading(true)
      const gridDataSource: IDatasource = {
        rowCount: undefined, // behave as infinite scroll

        async getRows(params: IGetRowsParams) {
          gridReadyParams.api.showLoadingOverlay()

          const { data } = await getApplications({
            ...parseFiltersAndSorting(initialParams, params, columnDefs),
            page: params.endRow / rowsPerPage,
          })

          if (loggedUser) {
            getAggregatedField({
              field: 'investment_payment',
              ...parseFiltersAndSorting(initialParams, params, columnDefs),
            }).then((res) => {
              setAggregateByInvesmentPayment(res.data?.investment_payment__sum ?? 0)
            })
          }

          gridReadyParams.api.hideOverlay()
          const rowsThisPage = data?.results ?? []
          const lastRow = data?.count ?? 0
          setTotalDeals(lastRow)
          params.successCallback(rowsThisPage, lastRow)
        },
      }
      gridReadyParams.api.setDatasource(gridDataSource)
      const columnState = localStorage.getItem('columnState')
      if (columnState && columnState !== 'undefined') {
        const localData = JSON.parse(columnState)
        if (localData) {
          gridReadyParams.columnApi.applyColumnState({ state: localData })
        }
      }

      setLoading(false)
    },
    [columnDefs]
  )

  const handleRowSelected = useCallback(
    (params) => {
      const selectedRows = params.api.getSelectedRows()
      if (selectedRows.length > 0) {
        const app = selectedRows.at(0)
        const matchingKeys = Object.keys(queries).filter((key) =>
          key.startsWith('getStreamingNotesByApplication')
        )
        const matchingKey = matchingKeys.find((key) => {
          const jsonPart = key.match(/\{.*\}/)
          if (jsonPart) {
            const parsed = JSON.parse(jsonPart[0])
            return parsed.application_id === app?.id
          }
          return false
        })
        const result: any = matchingKey ? queries[matchingKey] : null
        const calculatedPage = result
          ? Math.max(Math.ceil((result?.data?.results?.length || 0) / streamingNotesPageSize), 1)
          : 1

        dispatch(setDealAdvisorOpen(true))
        dispatch(setDealAdvisorTab('notes'))
        dispatch(setCurrentStreamingNotePage(calculatedPage))
        dispatch(setSelectedAppId(app?.external_id))
        setSelectedApplication({
          ...app,
          externalId: app?.external_id,
          uuid: app?.application_uuid,
        })
      } else {
        dispatch(setDealAdvisorOpen(false))
        dispatch(setSelectedAppId(null))
        setSelectedApplication(null)
        dispatch(setCurrentStreamingNotePage(1))
      }
    },
    [queries]
  )

  const isWorkviewsAdmin = isWorkviewsAdminOrSuperuser(loggedUser)

  return (
    <div className='relative h-full w-full ag-theme-alpine'>
      <ClosedLostDrawer
        isOpen={isClosedLossModal}
        application={application}
      />
      <>
        {isLoadingFields || loading ? (
          <WorkViewsSkeleton />
        ) : (
          <>
            {columnDefs && (
              <>
                <AgGridReact
                  ref={gridRef}
                  defaultColDef={defaultColDef}
                  columnDefs={columnDefs}
                  columnTypes={columnTypes}
                  onGridReady={onGridReady}
                  onDisplayedColumnsChanged={() =>
                    setVisibleColCount(gridRef.current.columnApi?.getAllDisplayedColumns().length)
                  }
                  getRowId={getRowId}
                  rowBuffer={0}
                  tooltipShowDelay={0}
                  rowModelType='infinite'
                  cacheBlockSize={rowsPerPage}
                  maxConcurrentDatasourceRequests={1}
                  rowSelection='single'
                  suppressContextMenu={!isWorkviewsAdmin}
                  enableRangeSelection={isWorkviewsAdmin}
                  onSelectionChanged={handleRowSelected}
                  stopEditingWhenCellsLoseFocus
                />
                {selectedApplication && (
                  <AddContactDrawer
                    isOpen={addContactDrawerOpen}
                    setIsOpen={setAddContactDrawerOpen}
                    application={selectedApplication}
                  />
                )}
              </>
            )}
          </>
        )}
      </>
    </div>
  )
}
